import { Context } from '../context/Context';
import { FullscreenQuad } from './FullscreenQuad';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';

const MIP_CHAIN_LENGTH = 5;

type MipLevel = {
  width: number;
  height: number;
  texture: RenderTexture;
  fbo: WebGLFramebuffer;
};

export class BloomPass {
  private mipChain: MipLevel[] = [];
  private sceneFbo!: WebGLFramebuffer;
  private sceneTexture!: RenderTexture;
  private sceneDepthRenderbuffer!: WebGLRenderbuffer;
  private width!: number;
  private height!: number;

  // Tunable parameters
  public threshold = 1.0;
  public strength = 0.04;
  public exposure = 1.0;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;

    // Scene color FBO — renders into RGBA16F for HDR range
    this.sceneTexture = new RenderTexture();
    this.sceneTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA16F,
      gl.RGBA,
      gl.HALF_FLOAT,
    );
    this.sceneFbo = createFramebuffer(gl, this.sceneTexture.glTexture!);

    // Attach depth renderbuffer so opaque passes can write gl_FragDepth
    this.sceneDepthRenderbuffer = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.sceneDepthRenderbuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, this.width, this.height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFbo);
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.RENDERBUFFER,
      this.sceneDepthRenderbuffer,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Create mip chain for progressive down/upsample
    let w = Math.max(1, this.width >> 1);
    let h = Math.max(1, this.height >> 1);
    this.mipChain = [];

    for (let i = 0; i < MIP_CHAIN_LENGTH; i++) {
      const tex = new RenderTexture();
      tex.glTexture = createColorTexture(gl, w, h, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT);
      const fbo = createFramebuffer(gl, tex.glTexture!);
      this.mipChain.push({ width: w, height: h, texture: tex, fbo });
      w = Math.max(1, w >> 1);
      h = Math.max(1, h >> 1);
    }
  }

  /** Returns the FBO that the scene should be rendered into. */
  getSceneFramebuffer(): WebGLFramebuffer {
    return this.sceneFbo;
  }

  /** Returns the scene texture (before bloom) for use by FXAA or other passes. */
  getSceneTexture(): RenderTexture {
    return this.sceneTexture;
  }

  /**
   * Run bloom extraction, downsample/upsample chain, and final composite
   * with ACES tonemapping. Writes result to outputBuffer.
   */
  render(
    ctx: Context,
    quad: FullscreenQuad,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    // === Downsample chain ===
    // First pass: threshold extraction + downsample from scene texture
    // Subsequent passes: just downsample from previous mip
    const downShader = ctx.engine.shaderManager.getBloomShader('DOWNSAMPLE');

    for (let i = 0; i < this.mipChain.length; i++) {
      const mip = this.mipChain[i];
      gl.bindFramebuffer(gl.FRAMEBUFFER, mip.fbo);
      gl.viewport(0, 0, mip.width, mip.height);

      ctx.setShader(downShader);

      const source = i === 0 ? this.sceneTexture : this.mipChain[i - 1].texture;
      const srcW = i === 0 ? this.width : this.mipChain[i - 1].width;
      const srcH = i === 0 ? this.height : this.mipChain[i - 1].height;

      downShader.uniformsContainer
        .setInputTexture(source)
        .setTexelSize([1.0 / srcW, 1.0 / srcH])
        .setThreshold(i === 0 ? this.threshold : 0.0);

      downShader.validate();
      quad.draw(gl);
    }

    // === Upsample chain ===
    // Progressively upsample from smallest mip back to largest, additively
    // blending into each level
    const upShader = ctx.engine.shaderManager.getBloomShader('UPSAMPLE');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);

    for (let i = this.mipChain.length - 2; i >= 0; i--) {
      const target = this.mipChain[i];
      const source = this.mipChain[i + 1];

      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      gl.viewport(0, 0, target.width, target.height);

      ctx.setShader(upShader);

      upShader.uniformsContainer
        .setInputTexture(source.texture)
        .setTexelSize([1.0 / source.width, 1.0 / source.height])
        .setBloomIntensity(1.0);

      upShader.validate();
      quad.draw(gl);
    }

    gl.disable(gl.BLEND);

    // === Final composite: scene + bloom with ACES tonemapping ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    const compShader = ctx.engine.shaderManager.getBloomShader('COMPOSITE');
    ctx.setShader(compShader);

    compShader.uniformsContainer
      .setInputTexture(this.sceneTexture)
      .setBloomTexture(this.mipChain[0].texture)
      .setBloomStrength(this.strength)
      .setExposure(this.exposure);

    compShader.validate();
    quad.draw(gl);
  }

  destroy(gl: WebGL2RenderingContext): void {
    for (const mip of this.mipChain) {
      gl.deleteFramebuffer(mip.fbo);
      if (mip.texture.glTexture) gl.deleteTexture(mip.texture.glTexture);
    }
    this.mipChain = [];

    if (this.sceneFbo) gl.deleteFramebuffer(this.sceneFbo);
    if (this.sceneDepthRenderbuffer) gl.deleteRenderbuffer(this.sceneDepthRenderbuffer);
    if (this.sceneTexture?.glTexture) gl.deleteTexture(this.sceneTexture.glTexture);
  }
}
